import { initials, metaOf, whyShort } from "../data/engine";
import type { Recommendation, SortKey, Work } from "../data/types";
import type { useKagami } from "../state/useKagami";
import { Chip, CoverArt, SignalBar, mutedColor } from "./primitives";

type K = ReturnType<typeof useKagami>;

const SORTS: [SortKey, string][] = [["match", "Match"], ["score", "Score"], ["pop", "Popularity"], ["new", "Newest"]];

// While searching, the default "Match" chip means relevance (the server's
// ordering) rather than taste-match, so the label reflects what it does.
const sortLabel = (key: SortKey, name: string, searching: boolean) =>
  searching && key === "match" ? "Relevance" : name;

function WorkCard({ item, seed, expanded, onToggleWhy, onOpen }: {
  item: Recommendation; seed: Work; expanded: boolean;
  onToggleWhy: () => void; onOpen: () => void;
}) {
  return (
    <article className="k-card card" style={{
      padding: 0, overflow: "hidden", background: "var(--color-surface)",
      boxShadow: "var(--shadow-sm)", gap: 0,
    }}>
      <CoverArt initials={initials(item.title)} coverPath={item.coverPath} onClick={onOpen}
        style={{ aspectRatio: "3 / 4" }}
        badge={
          <span style={{
            display: "inline-block",
            fontFamily: "var(--font-heading)", fontWeight: 600, fontSize: 11, padding: "3px 7px",
            borderRadius: 6, background: "color-mix(in srgb, var(--color-bg) 78%, transparent)",
            border: "1px solid var(--color-accent)", color: "var(--color-accent)",
          }}>{item.match}%</span>
        } />

      <div style={{ padding: "var(--k-pad)", display: "flex", flexDirection: "column", gap: 5 }}>
        <div onClick={onOpen} style={{
          fontFamily: "var(--font-heading)", fontWeight: 500, fontSize: "var(--k-cardfont)",
          lineHeight: 1.25, textWrap: "pretty", cursor: "pointer",
        }}>{item.title}</div>
        <div style={{
          fontSize: 11, color: mutedColor(48),
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>{metaOf(item)}</div>
        <div style={{ fontSize: 11.5, lineHeight: 1.4, color: "var(--color-accent-300)", textWrap: "pretty" }}>
          {/* Prefer the server's explanation: it knows which tags were
              actually shared and how many users co-recommended the pair,
              neither of which the client can reconstruct. */}
          {item.why || whyShort(item.signals, item, seed)}
        </div>
        <button className="k-chip" onClick={(e) => { e.stopPropagation(); onToggleWhy(); }} style={{
          alignSelf: "flex-start", border: 0, padding: "2px 0", fontSize: 11, color: mutedColor(55),
        }}>{expanded ? "Hide" : "Why this?"}</button>

        {expanded && (
          <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingTop: 2 }}>
            {item.signals.slice(0, 4).map((s) => <SignalBar key={s.key} signal={s} height={3} compact />)}
          </div>
        )}
      </div>
    </article>
  );
}

export function Discover({ k }: { k: K }) {
  if (!k.seed) return null;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <h3 style={{ margin: 0 }}>
          {k.filters.query ? `${k.results.length} result${k.results.length === 1 ? "" : "s"}` : "Recommended"}
        </h3>
        {k.searching && <span style={{ fontSize: 12, color: mutedColor(50) }}>searching…</span>}
        {k.recError && !k.filters.query && (
          <span style={{ fontSize: 12, color: "var(--color-accent-2-400)" }}>{k.recError}</span>
        )}
        {k.searchError && (
          <span style={{ fontSize: 12, color: "var(--color-accent-2-400)" }}>
            {k.searchError}
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 5 }}>
          <button className="k-chip k-mob" onClick={() => k.setFiltersOpen(true)}>Filters</button>
          {SORTS.map(([key, name]) => (
            <Chip key={key} on={k.sort === key} onClick={() => k.setSort(key)}>
              {sortLabel(key, name, k.isSearching)}
            </Chip>
          ))}
        </div>
      </div>

      <div className="k-grid">
        {k.results.map((item) => (
          <WorkCard key={item.id} item={item} seed={k.seed!} expanded={!!k.expanded[item.id]}
            onToggleWhy={() => k.toggleWhy(item.id)} onOpen={() => k.setSelId(item.id)} />
        ))}
      </div>

      {k.results.length === 0 && (
        <div style={{ padding: "56px 0", textAlign: "center", color: mutedColor(50), fontSize: 13 }}>
          {k.filters.query ? `Nothing matched “${k.filters.query}”.` : "No matches."}{" "}
          <button className="btn btn-ghost" onClick={k.clearFilters}>Clear filters</button>
        </div>
      )}
    </div>
  );
}
